import { createTheme } from "@mui/material/styles";

import InterVariable from "./fonts/Inter-Variable.woff2";
import JetBrainsMonoVariable from "./fonts/JetBrainsMono-Variable.woff2";
import {
  ACCENT,
  ACCENT_2,
  ACCENT_GRADIENT,
  ACCENT_TINT,
  ACCENT_TINT_STRONG,
  BG,
  BLUE,
  BORDER,
  BORDER_STRONG,
  FOCUS_BORDER,
  FOCUS_RING,
  FONT_STACK,
  GREEN,
  RADIUS_LG,
  RADIUS_MD,
  RADIUS_XL,
  RED,
  SHADOW_POP,
  SURFACE,
  SURFACE_2,
  TEXT,
  TEXT_FAINT,
  TEXT_MUTED,
  TEXT_SECONDARY,
  rootCssText,
} from "./designTokens";

/**
 * Neura dark theme.
 *
 * Values come from `designTokens.js`, which mirrors the design system's
 * `neura.css :root`. Nothing here should hard-code a colour.
 *
 * The block of non-standard keys at the bottom (`mainButton`, `tableStyle`, …)
 * is legacy: those are NOT MUI theme keys and are never read through
 * `useTheme()`. Components `import theme from "Global/theme"` and spread them
 * into `sx=` / `style=` directly, so the theme object doubles as a style
 * constants module. Only the seven keys still consumed anywhere in `src/`
 * survive here; the other ~25 were dropped as dead weight.
 */
const theme = createTheme({
  palette: {
    mode: "dark",

    background: {
      default: BG,
      paper: SURFACE,
    },

    primary: {
      main: ACCENT,
      contrastText: BG,
    },

    // Retained because existing components reference these palette slots.
    information: {
      main: ACCENT,
    },

    // Was red[500]. ResidentsAnalytics renders a KPI figure at
    // `color: secondary.main`, where a semantic red read as an error.
    secondary: {
      main: ACCENT_2,
    },

    error: {
      main: RED,
    },

    // The system has no warning token distinct from the accent — amber
    // warning is expressed with the brand gold itself.
    warning: {
      main: ACCENT,
      contrastText: BG,
    },

    success: {
      main: GREEN,
      contrastText: BG,
    },

    info: {
      main: BLUE,
    },

    cancel_button: {
      main: TEXT_MUTED,
      contrastText: TEXT,
    },

    filter_modal_form_control: {
      main: TEXT_SECONDARY,
    },

    button_page_link: {
      main: { textDecorationLine: "none", color: "inherit" },
    },

    text: {
      primary: TEXT,
      secondary: TEXT_SECONDARY,
      disabled: TEXT_FAINT,
    },

    divider: BORDER,
  },

  typography: {
    // Previously this listed the system UI stack and no font was ever loaded.
    // Inter is now self-hosted (see the @font-face rules below).
    fontFamily: FONT_STACK,
  },

  shape: {
    borderRadius: 8,
  },

  components: {
    MuiCssBaseline: {
      // A string rather than an object so the @font-face rules and the :root
      // custom-property block can be emitted verbatim. Those custom properties
      // are how the chrome reads tokens without importing them — AppLayout
      // styles almost everything as `var(--surface)` / `var(--accent)` — and
      // they are what any plain CSS or lifted mockup rule resolves against.
      styleOverrides: `
        @font-face {
          font-family: 'Inter';
          font-style: normal;
          font-weight: 100 900;
          font-display: swap;
          src: url(${InterVariable}) format('woff2');
        }

        @font-face {
          font-family: 'JetBrains Mono';
          font-style: normal;
          font-weight: 100 800;
          font-display: swap;
          src: url(${JetBrainsMonoVariable}) format('woff2');
        }

        :root {
          color-scheme: dark;
${rootCssText}
        }

        /* Every design-system page opens with this reset, and its layouts
           assume it — spacing is expressed as flex/grid gap, never as default
           heading and paragraph margins. Without it, markup lifted from a
           mockup picks up ~1em of browser margin it was never drawn with.
           Zero specificity, so no MUI component style is affected. */
        * { margin: 0; padding: 0; }

        html, body, #root {
          background: ${BG};
          color: ${TEXT};
          -webkit-font-smoothing: antialiased;
        }

        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-track { background: rgba(255, 255, 255, 0.03); }
        ::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.18); border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.3); }
      `,
    },

    // Card, Dialog and Menu all derive their surface from Paper. Without this
    // they fall back to MUI's default dark grey (#121212), not the system's.
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: SURFACE,
          backgroundImage: "none",
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            backgroundColor: "rgba(255, 255, 255, 0.04)",
            borderRadius: RADIUS_MD,
            "& fieldset": { borderColor: BORDER_STRONG },
            "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.16)" },
            "&.Mui-focused fieldset": {
              borderColor: FOCUS_BORDER,
              borderWidth: "1px",
            },
            "&.Mui-focused": { boxShadow: FOCUS_RING },
          },
          "& .MuiInputLabel-root": { color: TEXT_MUTED },
          "& .MuiInputLabel-root.Mui-focused": { color: ACCENT },
          "& .MuiInputBase-input": { color: TEXT },
          "& .MuiInputBase-input::placeholder": {
            color: TEXT_FAINT,
            opacity: 1,
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(255, 255, 255, 0.04)",
          borderRadius: RADIUS_MD,
          "& fieldset": { borderColor: BORDER_STRONG },
          "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.16)" },
          "&.Mui-focused fieldset": { borderColor: FOCUS_BORDER },
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: SURFACE_2,
          border: `1px solid ${BORDER_STRONG}`,
          borderRadius: RADIUS_XL,
          boxShadow: SHADOW_POP,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "0.85rem",
          borderRadius: RADIUS_MD,
          margin: "2px 6px",
          "&:hover": { backgroundColor: ACCENT_TINT },
          "&.Mui-selected": {
            backgroundColor: ACCENT_TINT,
            color: ACCENT,
            "&:hover": { backgroundColor: ACCENT_TINT_STRONG },
          },
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
        },
        containedPrimary: {
          background: ACCENT_GRADIENT,
          color: BG,
          borderRadius: RADIUS_LG,
          "&:hover": {
            background: ACCENT_GRADIENT,
            boxShadow: `0 6px 18px ${ACCENT_TINT_STRONG}`,
          },
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: SURFACE,
          borderRadius: "14px",
          border: `1px solid ${BORDER_STRONG}`,
          boxShadow: "0 24px 60px rgba(0, 0, 0, 0.5)",
          backgroundImage: "none",
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: { color: TEXT, fontWeight: 600, fontSize: "1.04rem" },
      },
    },

    MuiDialogContentText: {
      styleOverrides: { root: { color: TEXT_SECONDARY } },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: RADIUS_MD },
        standardError: {
          backgroundColor: "rgba(239, 68, 68, 0.08)",
          color: "#F87171",
          border: "1px solid rgba(239, 68, 68, 0.22)",
          "& .MuiAlert-icon": { color: "#F87171" },
        },
        standardWarning: {
          backgroundColor: ACCENT_TINT,
          color: ACCENT,
          border: `1px solid ${ACCENT_TINT_STRONG}`,
          "& .MuiAlert-icon": { color: ACCENT },
        },
        standardSuccess: {
          backgroundColor: "rgba(34, 197, 94, 0.10)",
          color: "#6EE7A0",
          border: "1px solid rgba(34, 197, 94, 0.25)",
          "& .MuiAlert-icon": { color: GREEN },
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: TEXT_MUTED,
          "&.Mui-checked": { color: ACCENT },
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": { color: ACCENT },
          "&.Mui-checked + .MuiSwitch-track": { backgroundColor: ACCENT },
        },
      },
    },

    MuiDivider: {
      styleOverrides: { root: { borderColor: BORDER } },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: SURFACE_2,
          border: `1px solid ${BORDER_STRONG}`,
          color: TEXT,
          fontSize: "0.74rem",
          boxShadow: SHADOW_POP,
        },
      },
    },
  },

  // ---------------------------------------------------------------------
  // Legacy style constants. Not MUI theme keys — consumed as
  // `sx={theme.mainButton.root}` after a direct `import theme from`.
  // Only the keys still referenced in src/ are kept.
  //
  // Colours are re-pointed at the tokens here; geometry and alignment are
  // left alone on purpose. Per-screen layout is Steps 2-5, so this step
  // changes what things are coloured, not where they sit.
  // ---------------------------------------------------------------------

  /** Primary action. Sign In, Add a User, Update, Submit. 8 call sites. */
  mainButton: {
    root: {
      fontFamily: FONT_STACK,
      border: 0,
      borderRadius: 20,
      background: ACCENT_GRADIENT,
      color: BG,
      fontWeight: 600,
      "&:hover": {
        background: ACCENT_GRADIENT,
        boxShadow: `0 6px 18px ${ACCENT_TINT_STRONG}`,
      },
    },
  },

  /**
   * Destructive action. Delete user, and the "No, Cancel" confirm button.
   * Was a solid #E54c38 fill; now the outlined danger pill the userport
   * mockup defines, which is the newer of the two conflicting `.btn-danger`
   * definitions in the design system.
   */
  cancelButtonStyle: {
    root: {
      fontFamily: FONT_STACK,
      background: "transparent",
      border: "1px solid rgba(239, 68, 68, 0.4)",
      borderRadius: 20,
      color: "#FCA5A5",
      fontWeight: 600,
      "&:hover": {
        background: "rgba(239, 68, 68, 0.1)",
        borderColor: "rgba(239, 68, 68, 0.7)",
        color: "#FEE2E2",
      },
    },
  },

  /** The current-page button in the users-table pagination group. */
  mainFilterButton: {
    root: {
      background: ACCENT_TINT_STRONG,
      border: `1px solid ${FOCUS_BORDER}`,
      color: ACCENT,
    },
  },

  /**
   * Users-table header. Was a full-width amber gradient bar with black text;
   * the design uses a near-flat header that reads as part of the surface.
   */
  tableNeuraDashboardStyle: {
    root: {
      background_default: "rgba(255, 255, 255, 0.02)",
      color: "rgba(255, 255, 255, 0.45)",
    },
  },

  tableStyle: {
    primaryTable_th: {
      fontSize: 13,
      height: 40,
      textAlign: "center",
    },

    // `color` was previously unset, so cell text inherited black-on-white.
    // On a dark surface that rendered near-invisible; it is now explicit.
    primaryTable_td: {
      fontSize: 13,
      height: 40,
      textAlign: "center",
      color: "rgba(255, 255, 255, 0.85)",
      borderBottom: `1px solid rgba(255, 255, 255, 0.04)`,
    },

    primaryTable_table: {
      border: "none",
      borderRadius: "4px",
      borderCollapse: "collapse",
      width: "100%",
      // Overflow hidden solved a major table corner rounding issue:
      overflow: "hidden",
      fontFamily: FONT_STACK,
    },

    overFlow: {
      maxWidth: "100%",
      overflowX: "auto",
    },
  },

});

export default theme;
